import {
    faBuildings,
    faClock,
    faEllipsisVertical,
    faSparkles,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Typography from "@vapor/react-extended/ExtendedTypography";
import {
    Box,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    Tooltip,
    useTheme,
} from "@vapor/react-material";
import { capitalize } from "lodash";
import { type ReactElement, useEffect, useState } from "react";

import { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { v4 } from "uuid";
import type { TaxPayerActivityCountersDto } from "../../../../../core/api/use-get-fulfilments/TaxPayerActivityCountersDto";
import { ActivityStatus } from "../../../../../core/dtos/ActivityStatus";
import { usePermission } from "../../../../../core/permissions/usePermission";
import { formatDate } from "../../../../../utils/dateUtils";
import { ActivityCounter } from "../../../../components/activity-counter/ActivityCounter";
import { DeleteCustomFulfilment } from "../../../../components/delete-custom-fulfilment/DeleteCustomFulfilment";
import { FulfilmentUnlinkAllTaxPayers } from "../fulfilment-unlink-all-tax-payers/FulfilmentUnlinkAllTaxPayers";

interface FulfilmentProps {
    date: Dayjs;
    description: string;
    isCustomFulfilment: boolean;
    isFirstFulfilmentInGroup?: boolean;
    levyId: string;
    year: number;
    onClick: (levyId: string) => void;
    onClickLinkTaxPayers: (levyId?: string) => unknown;
    onClickEditCustomFulfilment: (levyId?: string) => unknown;
    taxPayerActivityCounters: TaxPayerActivityCountersDto;
    title: string;
    withTaxTinderSuggestion?: boolean;
}

export const Fulfilment = ({
    taxPayerActivityCounters: activityCounters,
    date,
    description,
    isCustomFulfilment,
    isFirstFulfilmentInGroup: isFirst = false,
    levyId,
    year,
    onClick,
    onClickLinkTaxPayers,
    onClickEditCustomFulfilment,
    title,
    withTaxTinderSuggestion = false,
}: FulfilmentProps): ReactElement => {
    // states
    const theme = useTheme();
    const { t } = useTranslation();

    const canLinkTaxPayer = usePermission("taxPayerLink");
    const canUnlinkTaxPayer = usePermission("taxPayerUnlink");
    const canEditCustomFulfilment = usePermission("fulfilmentEdit");
    const canDeleteCustomFulfilment = usePermission("fulfilmentDelete");

    const [menuAnchorEl, setMenuAnchorEl] = useState<
        (EventTarget & HTMLButtonElement) | null
    >(null);

    const [fulfilmentTitleMaxWidth, setFulfilmentTitleMaxWidth] =
        useState("0px");
    const [fulfilmentStatusesId] = useState(v4());
    const [fulfilmentContainerId] = useState(v4());

    const onClickFulfilment = () => {
        onClick(levyId);
    };

    const onClickMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setMenuAnchorEl(null);
    };

    const onClickMenuItemLinkTaxPayers = (
        event: React.MouseEvent<HTMLElement>,
    ) => {
        event.stopPropagation();
        onClickLinkTaxPayers(levyId);
        handleCloseMenu(event);
    };

    const onClickMenuItemCommon = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
    };

    // Items built once: the kebab trigger derives from them
    // (menuItems.length), so it cannot drift from what the menu holds.
    const menuItems = [
        canLinkTaxPayer && (
            <MenuItem key="link" onClick={onClickMenuItemLinkTaxPayers}>
                {t("views.home.components.fulfilment.menu_items.link")}
            </MenuItem>
        ),
        canUnlinkTaxPayer && activityCounters.linked > 0 && (
            <MenuItem key="unlink-all" onClick={onClickMenuItemCommon}>
                <FulfilmentUnlinkAllTaxPayers
                    levyId={levyId}
                    year={year}
                    onCancel={handleCloseMenu}
                    onUnlinkAll={handleCloseMenu}
                ></FulfilmentUnlinkAllTaxPayers>
            </MenuItem>
        ),
        isCustomFulfilment && canEditCustomFulfilment && (
            <MenuItem
                key="edit"
                onClick={(e) => {
                    handleCloseMenu(e);
                    onClickEditCustomFulfilment();
                    onClickMenuItemCommon(e);
                }}
            >
                {t("views.home.components.fulfilment.menu_items.edit")}
            </MenuItem>
        ),
        isCustomFulfilment &&
            canDeleteCustomFulfilment &&
            activityCounters.total < 1 && (
                <MenuItem key="delete" onClick={onClickMenuItemCommon}>
                    <DeleteCustomFulfilment
                        levyId={levyId}
                        onCancel={handleCloseMenu}
                        onDelete={handleCloseMenu}
                    >
                        {t(
                            "views.home.components.fulfilment.menu_items.delete",
                        )}
                    </DeleteCustomFulfilment>
                </MenuItem>
            ),
    ].filter(Boolean);

    useEffect(() => {
        const handleResize = () => {
            const fulfilmentOffsetLeft =
                (document
                    .getElementById(fulfilmentContainerId)
                    ?.getBoundingClientRect().left || 0) * 2;
            const fulfilmentStatusesWidth =
                document.getElementById(fulfilmentStatusesId)?.clientWidth || 0;

            const widthVar = `calc(98vw - ${fulfilmentOffsetLeft}px - ${fulfilmentStatusesWidth}px)`;

            setFulfilmentTitleMaxWidth(widthVar);
        };
        window.addEventListener("resize", handleResize);
        handleResize();

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [fulfilmentContainerId, fulfilmentStatusesId]);

    return (
        <Stack
            direction="column"
            sx={{
                border: "1px solid #B9C8D0",
                borderRadius: isFirst ? "4px 4px 0 0" : undefined,
                borderTop: "none",
            }}
        >
            {isFirst && (
                <Stack
                    direction="row"
                    alignItems="center"
                    gap={1}
                    sx={{
                        background: theme.palette.primary.background,
                        borderRadius: isFirst ? "4px 4px 0 0" : undefined,
                        borderTop: "1px solid #B9C8D0",
                        paddingX: 2,
                        paddingY: 1,
                    }}
                >
                    <FontAwesomeIcon
                        icon={faClock}
                        color={theme.palette.primary.textTitleColor}
                    />
                    <Typography
                        color={theme.palette.primary.textTitleColor}
                        sx={{ lineHeight: 1 }}
                        variant="bodyLarge500"
                    >
                        {t("views.home.components.fulfilment.header.deadline")}
                    </Typography>
                    <Typography
                        color={theme.palette.primary.textTitleColor}
                        sx={{ lineHeight: 1 }}
                        variant="bodyLarge500"
                    >
                        {formatDate(date)}
                    </Typography>
                </Stack>
            )}
            <Stack
                direction="row"
                justifyContent="space-between"
                id={fulfilmentContainerId}
                onClick={onClickFulfilment}
                sx={{
                    border: "1px solid transparent",
                    cursor: "pointer",
                    height: "100%",
                    padding: 1.5,
                    ":hover": {
                        borderColor: "hsl(200, 100%, 42%)",
                    },
                }}
            >
                <Stack
                    maxWidth={fulfilmentTitleMaxWidth}
                    sx={{
                        background: theme.palette.primary.surfaceDefault,
                    }}
                >
                    <Stack
                        alignItems="center"
                        direction="row"
                        justifyContent="space-between"
                        minHeight={32}
                        mr={2}
                    >
                        <Stack
                            sx={{
                                cursor: "pointer",
                                display: "none",
                                width: 1,
                                ":hover": {
                                    width: "100%",
                                    color: "rgb(0, 144, 209)",
                                    display: "inline",
                                },
                                zIndex: "2",
                            }}
                            style={{
                                backgroundColor:
                                    theme.palette.primary.whiteSmoke,
                            }}
                        >
                            <Tooltip title={description}>
                                <Typography
                                    color={theme.palette.primary.textTitleColor}
                                    // variant="titleSmall"
                                >
                                    {capitalize(description)}
                                </Typography>
                            </Tooltip>
                        </Stack>
                        <Typography
                            color={theme.palette.text.primary}
                            variant="bodyLarge500"
                            noWrap
                        >
                            {description}
                        </Typography>
                    </Stack>
                    <Stack direction="row" gap={1} alignItems="center">
                        {withTaxTinderSuggestion && (
                            <Tooltip
                                title={t(
                                    "views.home.components.fulfilment.tax_tinder_suggestion.tooltip",
                                )}
                            >
                                <FontAwesomeIcon
                                    icon={faSparkles}
                                    color={
                                        theme.palette.primary.interactiveDefault
                                    }
                                />
                            </Tooltip>
                        )}
                        <Box
                            sx={{
                                background: theme.palette.primary.background,
                                width: "fit-content",
                                padding: 1,
                                borderRadius: "4px",
                            }}
                        >
                            <Stack direction="row" gap={1}>
                                <FontAwesomeIcon
                                    icon={faBuildings}
                                    color={theme.palette.primary.textTitleColor}
                                />
                                <Typography
                                    color={
                                        theme.palette.primary.textSubduedColor
                                    }
                                    variant="subtitle2"
                                    sx={{
                                        lineHeight: 1,
                                    }}
                                >
                                    {activityCounters.total}
                                </Typography>
                            </Stack>
                        </Box>
                        <Box
                            sx={{
                                background: theme.palette.primary.background,
                                width: "fit-content",
                                padding: 1,
                                borderRadius: "4px",
                            }}
                        >
                            <Typography
                                color={theme.palette.primary.textSubduedColor}
                                variant="subtitle2"
                                sx={{
                                    lineHeight: 1,
                                }}
                            >
                                {capitalize(title)}
                            </Typography>
                        </Box>
                    </Stack>
                </Stack>
                <Stack
                    alignSelf="center"
                    direction="row"
                    id={fulfilmentStatusesId}
                >
                    <Stack p={2} pt={1} pb={1}>
                        <Stack direction="row" spacing={2} justifyContent="end">
                            <Divider orientation="vertical" flexItem />
                            {activityCounters.linked > 0 && (
                                <ActivityCounter
                                    count={activityCounters.linked}
                                    status={ActivityStatus.LINKED}
                                />
                            )}
                            {activityCounters.pending > 0 && (
                                <ActivityCounter
                                    count={activityCounters.pending}
                                    status={ActivityStatus.PENDING}
                                />
                            )}
                            {activityCounters.closed > 0 && (
                                <ActivityCounter
                                    count={activityCounters.closed}
                                    status={ActivityStatus.CLOSED}
                                />
                            )}
                            {activityCounters.completed > 0 && (
                                <ActivityCounter
                                    count={activityCounters.completed}
                                    numberOfWarnings={
                                        activityCounters.completedWithWarnings
                                    }
                                    status={ActivityStatus.COMPLETED}
                                />
                            )}
                            <Divider orientation="vertical" flexItem />
                        </Stack>
                    </Stack>
                    <Stack direction="row" alignItems="center">
                        {menuItems.length > 0 && (
                            <>
                                <IconButton
                                    aria-controls="menu"
                                    aria-haspopup="true"
                                    onClick={onClickMenu}
                                    size="small"
                                >
                                    <FontAwesomeIcon
                                        color={
                                            theme.palette.primary
                                                .interactiveDefault
                                        }
                                        icon={faEllipsisVertical}
                                        size="xs"
                                    />
                                </IconButton>
                                <Menu
                                    id="menu"
                                    anchorEl={menuAnchorEl}
                                    open={
                                        menuItems.length > 0 && !!menuAnchorEl
                                    }
                                    onClose={handleCloseMenu}
                                    anchorOrigin={{
                                        vertical: "bottom",
                                        horizontal: "center",
                                    }}
                                    transformOrigin={{
                                        vertical: "top",
                                        horizontal: "right",
                                    }}
                                >
                                    {menuItems}
                                </Menu>
                            </>
                        )}
                    </Stack>
                </Stack>
            </Stack>
        </Stack>
    );
};
